/* Simple Entertainment — brand-layer behaviour for custom SE sections.
   Horizon owns navigation, cart and search; this only handles the enquiry
   form's server-rendered confirmation state. */
(function () {
  'use strict';
  function focusEnquiryConfirmation() {
    var confirmation = document.querySelector('[data-enquiry-confirmation]');
    if (!confirmation) return;
    confirmation.focus();
    confirmation.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  if (document.readyState !== 'loading') {
    focusEnquiryConfirmation();
  } else {
    document.addEventListener('DOMContentLoaded', focusEnquiryConfirmation);
  }
})();

/* FlexCon rental products — friendly missing-date message (theme layer only).
   If the FlexCon date field is visible and empty, the normal product-page
   Add-to-cart click/submit is stopped before app/theme handlers run and an
   accessible message is shown beside the date control. When a date is chosen
   the message clears and the normal add flow proceeds untouched. This never
   weakens FlexCon's server-side purchase-option enforcement: direct AJAX and
   /cart/VARIANT:1 permalink attempts are still rejected by Shopify (422). */
(function () {
  'use strict';
  var MSG_TEXT = 'Choose your rental date before adding this item to cart.';
  var MSG_CLASS = 'se-rental-msg';

  /* Resolved lazily so it works no matter when FlexCon hydrates its block. */
  function visibleDateInput() {
    var inputs = document.querySelectorAll('.card-rental input.flatpickr-input');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].offsetParent) return inputs[i]; // visible (e.g. Rent selected)
    }
    return null;
  }

  function ensureMessage(input) {
    var card = input.closest('.card-rental');
    var existing = card.querySelector('.' + MSG_CLASS);
    if (existing) return existing;
    var msg = document.createElement('p');
    msg.className = MSG_CLASS;
    msg.setAttribute('role', 'alert'); /* assertive live region */
    msg.setAttribute('tabindex', '-1');
    msg.hidden = true;
    msg.textContent = MSG_TEXT;
    var anchor = input.closest('.card-rental-datepicker') || input;
    anchor.insertAdjacentElement('afterend', msg);
    return msg;
  }

  function showMessage(input) {
    var msg = ensureMessage(input);
    msg.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    msg.focus({ preventScroll: true });
    msg.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearMessage() {
    var msgs = document.querySelectorAll('.' + MSG_CLASS);
    for (var i = 0; i < msgs.length; i++) msgs[i].hidden = true;
    var inputs = document.querySelectorAll('.card-rental input.flatpickr-input[aria-invalid]');
    for (var j = 0; j < inputs.length; j++) inputs[j].removeAttribute('aria-invalid');
  }

  function dateMissing() {
    var input = visibleDateInput();
    if (!input) return null; // no visible FlexCon date field -> not our concern
    return input.value.trim() === '' ? input : false;
  }

  function guard(event, submitEl) {
    var form = submitEl && submitEl.closest && submitEl.closest('form[action*="/cart/add"]');
    if (!form) return;
    var missing = dateMissing();
    if (missing === null) return;      // non-rental product page
    if (missing === false) {           // valid date chosen -> normal flow
      clearMessage();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();  // suppress the unclear app-side "Invalid format"
    showMessage(missing);
  }

  /* Capture phase on window: fires before document-level theme/app click
     handlers (window -> document -> target), so the blocked click never
     reaches Horizon's add animation or FlexCon's "Invalid format" path. */
  window.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button[name="add"]');
      if (btn) guard(e, btn);
    },
    true
  );
  /* Enter-key submits too. */
  window.addEventListener(
    'submit',
    function (e) {
      if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) {
        guard(e, e.target);
      }
    },
    true
  );
  /* Clear as soon as a valid date is selected (flatpickr fires change). */
  document.addEventListener(
    'change',
    function (e) {
      if (e.target && e.target.matches && e.target.matches('.card-rental input.flatpickr-input')) {
        if (dateMissing() === false) clearMessage();
      }
    },
    true
  );
})();

/* Order-level pickup preference — persisted as the Shopify cart attribute
   "Pickup preference" (one value per order, so a multi-product cart can never
   hold conflicting choices). The control is rendered by
   snippets/se-pickup-preference.liquid inside cart-summary, which serves both
   the cart drawer and the cart page. FlexCon line-item properties are never
   touched. Checkout is held until an in-flight update settles. */
(function () {
  'use strict';
  var ATTR = 'Pickup preference';
  var DEFAULT = 'Mount Eliza pickup — standard';
  var pending = 0;

  function statusEls() {
    return document.querySelectorAll('[data-se-pickup-status]');
  }
  function setStatus(text) {
    var els = statusEls();
    for (var i = 0; i < els.length; i++) els[i].textContent = text;
  }
  function setBusy(busy) {
    var buttons = document.querySelectorAll('button[name="checkout"]');
    for (var i = 0; i < buttons.length; i++) {
      /* Only manage the flag we set ourselves; never re-enable an empty cart. */
      if (busy) {
        buttons[i].setAttribute('data-se-pickup-busy', 'true');
        buttons[i].disabled = true;
      } else if (buttons[i].getAttribute('data-se-pickup-busy')) {
        buttons[i].removeAttribute('data-se-pickup-busy');
        /* Do NOT re-enable if the transmitter guard is holding checkout —
           the two guards must not clobber each other. */
        if (
          buttons[i].getAttribute('data-se-tx-blocked') !== 'true' &&
          buttons[i].getAttribute('data-se-window-blocked') !== 'true'
        ) {
          buttons[i].disabled = false;
        }
      }
    }
  }

  function updateAttribute(value) {
    var body = { attributes: {} };
    body.attributes[ATTR] = value;
    pending++;
    setBusy(true);
    setStatus('Updating pickup preference…');
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        setStatus('Pickup preference saved: ' + value);
        return cart;
      })
      .catch(function () {
        setStatus('Could not save your pickup preference. Please try again.');
      })
      .then(function (cart) {
        pending--;
        if (pending <= 0) {
          pending = 0;
          setBusy(false);
        }
        return cart;
      });
  }

  /* Keep every rendered copy of the control in sync (drawer + cart page). */
  function syncInputs(value) {
    var inputs = document.querySelectorAll('[data-se-pickup-input]');
    for (var i = 0; i < inputs.length; i++) inputs[i].checked = inputs[i].value === value;
  }

  /* ---------------- Mount Eliza pickup window (option 1) ----------------
     Mount Eliza pickup runs 24 hours, so the customer nominates a preferred
     2-hour window. Liquid renders each option's value as the whole readable
     window ("6:00 pm – 8:00 pm"), so nothing is derived or formatted here and
     the attribute staff see needs no interpretation.

     The window is required for Mount Eliza and meaningless for the other two, so
     switching away clears it — an order collected from Rosebud or delivered must
     never carry a stale Mount Eliza window. */
  var WINDOW_ATTR = 'Pickup window';

  function isPickupSelected() {
    var checked = document.querySelector('[data-se-pickup-input]:checked');
    if (!checked) return false;
    if (checked.value.indexOf('Rosebud') !== -1) return false;
    /* While delivery is switched off there is no delivery radio to select, so
       anything that is not Rosebud is Mount Eliza. */
    if (!deliveryEnabled()) return true;
    return checked.value !== deliveryValue();
  }

  function selectedWindow() {
    var sel = document.querySelector('[data-se-window-select]');
    return sel ? sel.value.trim() : '';
  }

  function syncWindowInputs(value) {
    var els = document.querySelectorAll('[data-se-window-select]');
    for (var i = 0; i < els.length; i++) els[i].value = value || '';
  }

  function updateWindow(value) {
    var body = { attributes: {} };
    body.attributes[WINDOW_ATTR] = value;
    pending++;
    setBusy(true);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        setStatus(value ? 'Pickup window saved: ' + value : 'Pickup window cleared.');
        return cart;
      })
      .catch(function () {
        setStatus('Could not save your pickup window. Please try again.');
      })
      .then(function (cart) {
        pending--;
        if (pending <= 0) { pending = 0; setBusy(false); }
        refreshWindowGate();
        return cart;
      });
  }

  /* Third checkout flag. The pickup-busy and transmitter guards each check the
     others before re-enabling, so all three must agree before checkout opens. */
  function refreshWindowGate() {
    var needed = isPickupSelected() && !selectedWindow();
    var buttons = document.querySelectorAll('button[name="checkout"]');
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      if (needed) {
        b.setAttribute('data-se-window-blocked', 'true');
        b.disabled = true;
      } else if (b.getAttribute('data-se-window-blocked') === 'true') {
        b.setAttribute('data-se-window-blocked', 'false');
        if (
          b.getAttribute('data-se-tx-blocked') !== 'true' &&
          b.getAttribute('data-se-pickup-busy') !== 'true'
        ) {
          b.disabled = false;
        }
      }
    }
    var panels = document.querySelectorAll('[data-se-window-panel]');
    for (var j = 0; j < panels.length; j++) {
      panels[j].classList.toggle('se-window-panel--required', needed);
    }
  }

  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches('[data-se-window-select]')) return;
    var v = el.value.trim();
    syncWindowInputs(v);
    updateWindow(v);
  });

  /* Explain the block rather than leaving a dead button. */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"]');
      if (!btn) return;
      if (isPickupSelected() && !selectedWindow()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setStatus('Choose your preferred 2-hour pickup window before checking out.');
        var sel = document.querySelector('[data-se-window-select]');
        if (sel) sel.focus();
      }
    },
    true
  );

  /* ---------------- delivery request (option 3) ----------------
     Delivery is a REQUEST, never a charge. Selecting it stores the suburb in a
     second cart attribute; switching back to a pickup option clears it. Cart
     line items are never touched by any of this, so a customer can move between
     options as often as they like without losing their booking. */
  var SUBURB_ATTR = 'Delivery suburb/postcode';

  function deliveryValue() {
    var fs = document.querySelector('[data-se-pickup]');
    return (fs && fs.getAttribute('data-se-delivery-value')) || '';
  }

  /* Delivery & Collection is PARKED for launch. The whole workflow stays in the
     code and comes back with the theme setting alone — nothing here is deleted
     or bypassed, it is simply not offered while the setting is off. */
  function deliveryEnabled() {
    var fs = document.querySelector('[data-se-pickup]');
    return !!fs && fs.getAttribute('data-se-delivery-enabled') === 'true';
  }

  function togglePanels(value) {
    var dv = deliveryValue();
    var panels = document.querySelectorAll('[data-se-delivery-panel]');
    for (var i = 0; i < panels.length; i++) panels[i].hidden = !(dv && value === dv);
    /* Mount Eliza is anything that is neither delivery nor Rosebud — and while
       delivery is off, anything that is not Rosebud. */
    var isPickup =
      value.indexOf('Rosebud') === -1 && (!deliveryEnabled() || value !== dv);
    var wp = document.querySelectorAll('[data-se-window-panel]');
    for (var j = 0; j < wp.length; j++) wp[j].hidden = !isPickup;
  }

  function updateSuburb(value) {
    var body = { attributes: {} };
    body.attributes[SUBURB_ATTR] = value;
    pending++;
    setBusy(true);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        setStatus(value ? 'Delivery suburb saved: ' + value : 'Delivery suburb cleared.');
        return cart;
      })
      .catch(function () {
        setStatus('Could not save your delivery suburb. Please try again.');
      })
      .then(function (cart) {
        pending--;
        if (pending <= 0) { pending = 0; setBusy(false); }
        return cart;
      });
  }

  /* Keep every rendered copy of the suburb field in sync (drawer + cart page). */
  function syncSuburbInputs(value) {
    var els = document.querySelectorAll('[data-se-delivery-suburb]');
    for (var i = 0; i < els.length; i++) {
      if (els[i] !== document.activeElement) els[i].value = value || '';
    }
  }

  var suburbTimer = null;
  document.addEventListener('input', function (e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches('[data-se-delivery-suburb]')) return;
    /* Debounced: one cart update when typing settles, not one per keystroke. */
    clearTimeout(suburbTimer);
    var v = el.value.trim();
    suburbTimer = setTimeout(function () {
      syncSuburbInputs(v);
      updateSuburb(v);
    }, 600);
  });
  /* Save immediately on blur so a customer who types and taps Checkout is not
     racing the debounce. */
  document.addEventListener(
    'blur',
    function (e) {
      var el = e.target;
      if (!el || !el.matches || !el.matches('[data-se-delivery-suburb]')) return;
      clearTimeout(suburbTimer);
      var v = el.value.trim();
      syncSuburbInputs(v);
      updateSuburb(v);
    },
    true
  );

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('[data-se-pickup-input]')) return;
    syncInputs(input.value);
    togglePanels(input.value);
    updateAttribute(input.value);
    /* Moving away from delivery clears the suburb so a stale value can never
       reach an order that is being collected from Mount Eliza or Rosebud. */
    if (input.value !== deliveryValue()) {
      syncSuburbInputs('');
      updateSuburb('');
    } else {
      var field = document.querySelector('[data-se-delivery-suburb]');
      if (field) field.focus();
    }
    /* Same rule for the pickup window: it only means anything for Mount Eliza. */
    if (!isPickupSelected()) {
      syncWindowInputs('');
      updateWindow('');
    }
    refreshWindowGate();
  });

  /* Ensure a non-empty cart always carries an explicit default. */
  function ensureDefault() {
    if (!document.querySelector('[data-se-pickup]')) return;
    fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || cart.item_count === 0) return;
        var current = cart.attributes && cart.attributes[ATTR];

        /* Stale cart: it still says delivery, but delivery is switched off. The
           customer would otherwise be stuck in a state with no visible option
           selected and no way back. Normalise to Mount Eliza, drop the suburb,
           and let the usual pickup-window requirement apply. */
        if (current && current === deliveryValue() && !deliveryEnabled()) {
          updateAttribute(DEFAULT);
          syncInputs(DEFAULT);
          togglePanels(DEFAULT);
          syncSuburbInputs('');
          updateSuburb('');
          syncWindowInputs(cart.attributes[WINDOW_ATTR] || '');
          refreshWindowGate();
          setStatus('Delivery is not available — your pickup is set to Mount Eliza.');
          return;
        }

        /* Lighting or sound in the cart: Rosebud is not offered, so a Rosebud
           choice made earlier (before those items were added) is reset. */
        var fs = document.querySelector('[data-se-pickup]');
        if (fs && fs.getAttribute('data-se-me-only') === 'true' && current && /rosebud/i.test(current)) {
          updateAttribute(DEFAULT);
          syncInputs(DEFAULT);
          togglePanels(DEFAULT);
          syncWindowInputs(cart.attributes[WINDOW_ATTR] || '');
          refreshWindowGate();
          setStatus('Lighting and sound are collected from Mount Eliza only — your pickup is set to Mount Eliza.');
          return;
        }

        if (!current) {
          updateAttribute(DEFAULT);
          togglePanels(DEFAULT);
        } else {
          syncInputs(current);
          togglePanels(current);
          syncSuburbInputs(cart.attributes[SUBURB_ATTR] || '');
          syncWindowInputs(cart.attributes[WINDOW_ATTR] || '');
        }
        refreshWindowGate();
      })
      .catch(function () {});
  }

  /* Belt-and-braces: if a click lands while an update is still in flight,
     hold it and let the update finish first. */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"]');
      if (btn && pending > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setStatus('Saving your pickup preference — try checkout again in a moment.');
      }
    },
    true
  );

  if (document.readyState !== 'loading') ensureDefault();
  else document.addEventListener('DOMContentLoaded', ensureDefault);
  /* The cart drawer re-renders its summary; re-check after those updates. */
  document.addEventListener('cart:update', ensureDefault);
})();

/* Immediate clamp for typed and pasted quantities.

   Horizon already clamps on blur, on the +/- buttons and on native keyboard
   stepping, and snippets/quantity-selector.liquid now publishes the real
   inventory ceiling as the input's `max`. The remaining gap is typing or
   pasting a larger number: until the field blurs, the input — and so the
   headset summary above it — shows a quantity that cannot be ordered.

   The limit is not recomputed here. It is read from the component's own
   getEffectiveMax(), so Horizon stays the single source of truth for what
   "maximum" means (it subtracts what is already in the cart).

   Registered BEFORE the readout below so the clamp lands before the summary
   reads the value; otherwise the summary would render one keystroke stale. */
(function () {
  'use strict';

  function clamp(input) {
    var component = input.closest('quantity-selector-component');
    if (!component || typeof component.getEffectiveMax !== 'function') return;

    var max = component.getEffectiveMax();
    if (max !== null && max !== undefined) {
      var n = parseInt(input.value, 10);
      if (isFinite(n) && n > max) input.value = String(max);
    }

    /* Refresh on every keystroke, not only when the value was clamped. Horizon
       normally only recomputes button state on blur, which was invisible while
       no maximum existed; now that one does, typing a smaller number after
       sitting at the ceiling would otherwise leave "+" disabled until blur.
       The lower bound is deliberately left to Horizon's blur handler — clamping
       an empty field to 1 mid-edit would stop anyone clearing it to retype. */
    if (typeof component.updateButtonStates === 'function') component.updateButtonStates();
  }

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches('input[name="quantity"]')) clamp(t);
  });
})();

/* Headset box quantity readout — "2 boxes (50 headsets)".
   Reads the live quantity input and the per-box figure published by
   snippets/se-box-quantity.liquid. Horizon owns the stepper and Shopify owns
   the inventory cap; this only reports what the current value means. */
(function () {
  'use strict';
  var el = document.querySelector('[data-se-box-qty]');
  if (!el) return;
  var perBox = parseInt(el.getAttribute('data-per-box'), 10);
  if (!(perBox > 0)) return;

  function form() {
    return el.closest('form[action*="/cart/add"]') || document;
  }

  function render() {
    var input = form().querySelector('input[name="quantity"]');
    var n = input ? parseInt(input.value, 10) : 1;
    if (!isFinite(n) || n < 1) n = 1;
    el.textContent = n + (n === 1 ? ' box \u00b7 ' : ' boxes \u00b7 ') + n * perBox + ' headsets';
  }

  /* The stepper updates the input programmatically as well as on typing, so
     watch the value directly rather than relying on change events alone. */
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) render();
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) render();
  });
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.quantity-minus, .quantity-plus')) {
      setTimeout(render, 0);
    }
  });

  if (document.readyState !== 'loading') render();
  else document.addEventListener('DOMContentLoaded', render);
})();

/* Live order total — "Your price for the night: $315".
   Reads the headset and transmitter prices and shop.money_format published by
   snippets/se-price-total.liquid, the live quantity input and the selected
   transmitter radio, and shows the combined figure before Add to cart. Pure
   readout: it never changes the form, the cart or any price. */
(function () {
  'use strict';
  var el = document.querySelector('[data-se-total]');
  if (!el) return;
  var headset = parseInt(el.getAttribute('data-headset-price'), 10);
  var tx = parseInt(el.getAttribute('data-tx-price'), 10);
  var perBox = parseInt(el.getAttribute('data-per-box'), 10);
  var format = el.getAttribute('data-money-format') || '${{amount}}';
  if (!(headset >= 0) || !(tx >= 0)) return;
  var valueEl = el.querySelector('[data-se-total-value]');
  var breakdownEl = el.querySelector('[data-se-total-breakdown]');

  function form() {
    return el.closest('form[action*="/cart/add"]') || document;
  }
  function money(cents) {
    var units = cents / 100;
    var withDecimals = units.toFixed(2);
    var noDecimals = Math.round(units).toString();
    var parts = withDecimals.split('.');
    var grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var groupedNo = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount_with_comma_separator}}', grouped.replace(/,/g, '.') + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', groupedNo.replace(/,/g, '.'))
      .replace('{{amount_no_decimals}}', groupedNo)
      .replace('{{amount}}', grouped + '.' + parts[1]);
  }
  function render() {
    var f = form();
    var q = f.querySelector('input[name="quantity"]');
    var n = q ? parseInt(q.value, 10) : 1;
    if (!isFinite(n) || n < 1) n = 1;
    var checked = f.querySelector('[data-se-tx-input]:checked');
    var t = checked ? parseInt(checked.value, 10) : 1;
    if (!(t >= 1 && t <= 3)) t = 1;
    if (valueEl) valueEl.textContent = money(n * headset + t * tx);
    if (breakdownEl) {
      breakdownEl.textContent =
        n + (n === 1 ? ' box' : ' boxes') +
        (perBox > 0 ? ' (' + n * perBox + ' headsets)' : '') +
        ' + ' + t + (t === 1 ? ' transmitter' : ' transmitters') +
        ' \u00b7 pickup free';
    }
  }
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) render();
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[name="quantity"], [data-se-tx-input]')) render();
  });
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.quantity-minus, .quantity-plus')) setTimeout(render, 0);
  });
  if (document.readyState !== 'loading') render();
  else document.addEventListener('DOMContentLoaded', render);
})();

/* Price my party hand-off.
   1) On the headset page, ?boxes=N&tx=M (from the calculator) preselects the
      quantity and the transmitter radio, within the page's own limits.
   2) ?extras=handle:qty,... is stored in sessionStorage and rendered as a
      "Still to add" checklist here and in the cart, each line linking to that
      product's page. Items already in the cart are ticked. Nothing is added
      automatically — FlexCon validates each product's date on its own page. */
(function () {
  'use strict';
  var KEY = 'se_party';
  function readParty() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function writeParty(p) {
    try { sessionStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {}
  }
  function money(cents) {
    var el = document.querySelector('[data-se-total], [data-se-estimator]');
    var format = (el && el.getAttribute('data-money-format')) || '${{amount}}';
    var parts = (cents / 100).toFixed(2).split('.');
    var grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace('{{amount_no_decimals}}', Math.round(cents / 100)).replace('{{amount}}', grouped + '.' + parts[1]);
  }

  /* ---- 1. presets on the headset page ---- */
  var params = new URLSearchParams(window.location.search);
  var form = document.querySelector('form[action*="/cart/add"] [data-se-tx]') ?
    document.querySelector('form[action*="/cart/add"] [data-se-tx]').closest('form') : null;
  if (form) {
    var boxes = parseInt(params.get('boxes'), 10);
    var qty = form.querySelector('input[name="quantity"]');
    if (qty && boxes >= 1) {
      var max = parseInt(qty.max, 10);
      if (isFinite(max) && max > 0) boxes = Math.min(boxes, max);
      qty.value = boxes;
      qty.dispatchEvent(new Event('input', { bubbles: true }));
      qty.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var tx = parseInt(params.get('tx'), 10);
    if (tx >= 1 && tx <= 3) {
      var radio = form.querySelector('[data-se-tx-input][value="' + tx + '"]');
      if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
    }
  }
  var extrasParam = params.get('extras');
  if (extrasParam !== null && document.querySelector('form[action*="/cart/add"]')) {
    (function () {
      var party = readParty() || { extras: [] };
      var wanted = {};
      extrasParam.split(',').forEach(function (pair) {
        var m = pair.match(/^([a-z0-9-]+):(\d+)$/i);
        if (m) wanted[m[1]] = parseInt(m[2], 10);
      });
      /* Keep titles/prices the calculator stored; the URL only confirms which. */
      party.extras = (party.extras || []).filter(function (e) { return wanted[e.handle]; }).map(function (e) { e.qty = wanted[e.handle]; return e; });
      Object.keys(wanted).forEach(function (h) {
        if (!party.extras.some(function (e) { return e.handle === h; })) party.extras.push({ handle: h, title: h.replace(/-/g, ' '), url: '/products/' + h, price: 0, qty: wanted[h] });
      });
      writeParty(party);
    })();
  }

  /* ---- 1b. carry the date into FlexCon's own picker ---- */
  var dateParam = params.get('date');
  if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
    try { var pp = readParty() || {}; pp.date = dateParam; writeParty(pp); } catch (e) {}
    var p = dateParam.split('-');
    var wanted = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
    var tries = 0, attempt = 0;
    function rentalPropsFilled() {
      var list = document.querySelectorAll('form[action*="/cart/add"] input[name="properties[_flex_conversion_start_date]"]');
      for (var k = 0; k < list.length; k++) if (list[k].value) return true;
      return false;
    }
    /* Goes through FlexCon's own picker (setDate fires its onChange, exactly
       like a manual pick), so FlexCon still owns validation and writes its
       rental properties itself. Fixed-duration products (headsets,
       transmitter, microphone, par can) want ONE selected day and work out
       the return themselves; variable products (lights, speakers, hazer)
       want a start and an end. We try one day first, then start=end, until
       FlexCon has written the properties. Never touches the picker before
       FlexCon's handler is attached, and stops after ~20 s. */
    (function presetDate() {
      if (rentalPropsFilled()) return;
      if (tries++ > 80) return;
      var input = document.querySelector('.card-rental input.flatpickr-input');
      var fp = input && input._flatpickr;
      var ready = fp && fp.config && fp.config.onChange && fp.config.onChange.length > 0;
      if (ready && tries % 4 === 0) {
        attempt++;
        try {
          if (attempt % 2 === 1) fp.setDate([wanted], true);
          else fp.setDate([wanted, wanted], true);
        } catch (e) {}
      }
      setTimeout(presetDate, 250);
    })();
  }

  /* ---- 2. checklist ---- */
  function renderChecklist() {
    var boxes = document.querySelectorAll('[data-se-extras]');
    if (!boxes.length) return;
    var party = readParty();
    var extras = party && party.extras ? party.extras.filter(function (e) { return e.qty > 0; }) : [];
    if (!extras.length) { boxes.forEach(function (b) { b.hidden = true; b.textContent = ''; }); return; }
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; })
      .then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (i) { inCart[i.handle] = (inCart[i.handle] || 0) + i.quantity; });
        var onCartPage = !!document.querySelector('[data-se-extras]:not(form [data-se-extras])') && !form;
        var headsetInCart = (cart.items || []).some(function (i) { return /silent-disco-bundle|headset/i.test(i.handle); });
        boxes.forEach(function (box) {
          box.textContent = '';
          var h = document.createElement('p');
          h.className = 'se-extras__title';
          h.textContent = form && !headsetInCart ? 'After you add the headsets, add the rest of your night:' : 'Still to add to your night:';
          box.appendChild(h);
          var ul = document.createElement('ul');
          ul.className = 'se-extras__list';
          var remaining = 0;
          extras.forEach(function (e) {
            var li = document.createElement('li');
            var done = (inCart[e.handle] || 0) >= e.qty;
            li.className = 'se-extras__item' + (done ? ' se-extras__item--done' : '');
            var label = document.createElement('span');
            label.textContent = (done ? '✓ ' : '') + e.qty + '× ' + e.title + (e.price ? ' — ' + money(e.price * e.qty) : '');
            li.appendChild(label);
            if (!done) {
              remaining++;
              var here = window.location.pathname.indexOf('/products/' + e.handle) >= 0;
              if (here) {
                var hs = document.createElement('span');
                hs.className = 'se-extras__here';
                hs.textContent = 'this page — add it above';
                li.appendChild(hs);
              } else {
                var a = document.createElement('a');
                a.className = 'se-extras__link';
                a.href = e.url + '?qty=' + e.qty + (party && party.date ? '&date=' + party.date : '');
                a.textContent = 'Add →';
                li.appendChild(a);
              }
            }
            ul.appendChild(li);
          });
          box.appendChild(ul);
          var note = document.createElement('p');
          note.className = 'se-extras__note';
          note.textContent = remaining ? 'Use the same date for each. Each item is checked for availability on its own page.' : 'All added — your night is in the cart.';
          box.appendChild(note);
          var clear = document.createElement('button');
          clear.type = 'button'; clear.className = 'se-extras__clear'; clear.textContent = 'Clear this list';
          clear.addEventListener('click', function () { try { sessionStorage.removeItem(KEY); } catch (err) {} renderChecklist(); });
          box.appendChild(clear);
          box.hidden = false;
        });
      });
  }
  if (document.readyState !== 'loading') renderChecklist();
  else document.addEventListener('DOMContentLoaded', renderChecklist);
  /* The cart drawer re-renders its summary on every change; redraw with it. */
  document.addEventListener('cart:update', function () { setTimeout(renderChecklist, 50); });
  document.addEventListener('CartUpdate', function () { setTimeout(renderChecklist, 50); });
  var mo = new MutationObserver(function () {
    if (document.querySelector('[data-se-extras][hidden]:empty')) renderChecklist();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  /* A product page reached from the checklist: preset its quantity too. */
  var q = parseInt(params.get('qty'), 10);
  if (!form && q >= 1) {
    var input = document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    if (input) {
      var mx = parseInt(input.max, 10);
      input.value = isFinite(mx) && mx > 0 ? Math.min(q, mx) : q;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
})();

/* ==================================================================== *
   TRANSMITTER REQUIREMENT — ⚠️ INTERIM CLIENT-SIDE PROTECTION ONLY.
   THIS IS NOT AUTHORITATIVE ENFORCEMENT AND IS BYPASSABLE.

   Direct /checkout, cart permalinks, raw /cart/add.js, the Storefront API
   and PayPal express all bypass everything below, because this is
   JavaScript the customer is not required to run.

   A custom Shopify Cart & Checkout Validation Function — the correct
   server-side answer — is NOT available to this store: public App Store
   apps containing Functions may run on Basic, but CUSTOM apps containing
   Functions require Shopify Plus, and this store is Basic with a custom
   app. Authoritative enforcement must come from native FlexCon support or
   a public validation app.
   See docs/TRANSMITTER_DEPENDENCY_DECISION_PACK.md.

   Rule: every distinct headset rental DATE GROUP needs >= 1 transmitter.
   One per date group — NOT one per box. 1–3 transmitters allowed.
 * ==================================================================== */
(function () {
  'use strict';
  /* ── Configuration comes from Shopify, never from constants here ──────
     snippets/se-tx-config.liquid publishes these on every page from two theme
     settings, so a transmitter price change or a recreated variant is picked up
     automatically. Nothing about the rule itself is configurable: one
     transmitter minimum per rental-date group, 1–3 channels, never one per box.

     If the config element is absent — settings unset, product unpublished, no
     available variant — every branch below is skipped. The selector does not
     render, no warning appears, and Add to cart behaves normally. A theme
     misconfiguration must never block a customer from checking out, and
     FlexCon's server-side rental-date enforcement is unaffected either way.
     ─────────────────────────────────────────────────────────────────── */
  var cfgEl = document.querySelector('[data-se-tx-config]');
  if (!cfgEl) return;

  var HEADSETS = (cfgEl.getAttribute('data-headset-variants') || '')
    .split(',')
    .map(function (s) { return parseInt(s, 10); })
    .filter(function (n) { return n > 0; });
  var TX = parseInt(cfgEl.getAttribute('data-tx-variant'), 10);

  if (!HEADSETS.length || !(TX > 0)) return;

  /* Every variant of the headset product counts, so the guard keeps working if
     the product later gains variants. */
  function isHeadset(id) {
    return HEADSETS.indexOf(id) !== -1;
  }

  var MSG =
    'A transmitter is required for your Silent Disco Headsets. Choose 1–3 transmitters for 1–3 music channels.';
  var ADD_URL = '/cart/add.js';

  /* ── Untrusted input ──────────────────────────────────────────────────
     Cart line-item properties are NOT trusted. Anyone can put arbitrary
     strings into them with a hand-made POST to /cart/add.js, and /cart.js
     will hand them straight back to us. So cart-derived values are:
       • never interpolated into HTML — every node below is built with
         document.createElement and filled with textContent;
       • never written into an attribute — the correction button carries an
         internal token ("g0", "g1", …) that we mint ourselves, and the real
         group key is looked up from a map that never reaches the DOM;
       • never used as a plain-object key — the group map is
         Object.create(null), so a property of "__proto__" cannot reach
         Object.prototype.
     ─────────────────────────────────────────────────────────────────── */

  /* --- date-group key: exact string match on FlexCon's own properties.
         No date parsing, no timezone maths, no invented values. --- */
  function groupKey(props) {
    props = props || {};
    return [
      props._flex_conversion_start_date || '',
      props._flex_conversion_end_date || '',
      props._flex_conversion_duration_id || '',
    ].join('|');
  }

  function analyse(cart) {
    /* Null-prototype: a line property of "__proto__" or "constructor" is then
       just an ordinary key and cannot pollute Object.prototype. */
    var groups = Object.create(null);
    (cart.items || []).forEach(function (i) {
      var k = groupKey(i.properties);
      if (!Object.prototype.hasOwnProperty.call(groups, k)) {
        groups[k] = { key: k, headsets: 0, transmitters: 0, props: i.properties || {} };
      }
      if (isHeadset(i.variant_id)) groups[k].headsets += i.quantity;
      if (i.variant_id === TX) groups[k].transmitters += i.quantity;
    });
    var invalid = Object.keys(groups)
      .map(function (k) { return groups[k]; })
      /* only groups that actually contain headsets are ever our concern */
      .filter(function (g) { return g.headsets > 0 && g.transmitters === 0; });
    return { groups: groups, invalid: invalid };
  }

  function txPropsFrom(props) {
    /* Reuse FlexCon's OWN values from the headset line. Nothing fabricated.
       These go back to Shopify as JSON, which validates them server-side;
       they are never rendered as markup. */
    return {
      _flex_conversion_purchase_option: props._flex_conversion_purchase_option || '1',
      _flex_conversion_start_date: props._flex_conversion_start_date,
      _flex_conversion_end_date: props._flex_conversion_end_date,
      _flex_conversion_duration_id: props._flex_conversion_duration_id,
    };
  }

  /* A rental date is only shown when it still looks like the ISO date FlexCon
     actually writes. textContent already makes any string inert, so this is a
     second line of defence against a hostile property being used to spoof or
     wreck the warning's layout. A value that fails the test is simply not
     displayed — the warning itself is unaffected. */
  function displayableDate(value) {
    if (typeof value !== 'string') return '';
    return /^\d{4}-\d{2}-\d{2}([ T][\d:]{4,8})?$/.test(value) ? value : '';
  }

  /* Shopify's own error text, also rendered with textContent only. */
  function shopifyError(data, status) {
    var text = data && (data.description || data.message);
    if (typeof text === 'string' && text) return text;
    return 'Sorry — that could not be added to your cart (error ' + status + '). Please try again.';
  }

  function readJson(response) {
    return response
      .json()
      .catch(function () { return {}; })
      .then(function (data) { return { ok: response.ok, status: response.status, data: data }; });
  }

  /* ---------------- product page: paired add ---------------- */
  function headsetForm(node) {
    /* Scope everything to the form that was actually clicked, so a page with
       more than one product form can never cross-read another form's values. */
    var form = node && node.closest ? node.closest('form[action*="/cart/add"]') : null;
    if (!form || !form.querySelector('[data-se-tx]')) return null;
    return form;
  }

  function selectedTxCount(scope) {
    var el = (scope || document).querySelector('[data-se-tx-input]:checked');
    var n = el ? parseInt(el.value, 10) : NaN;
    return n >= 1 && n <= 3 ? n : 1;
  }

  /* The variant the form itself is set to add. Read from the form rather than
     assumed, so if the headset product ever gains variants the customer's own
     selection is what gets added. Falls back to the first configured headset
     variant, which is the single-variant case this store uses today. */
  function headsetVariantId(form) {
    var input = form && form.querySelector('input[name="id"], select[name="id"]');
    var n = input ? parseInt(input.value, 10) : NaN;
    return isHeadset(n) ? n : HEADSETS[0];
  }

  /* The customer's own quantity selector on the headset form. Horizon owns the
     stepper and Shopify owns inventory; we only read the value. */
  function headsetQty(form) {
    var input = form && form.querySelector('input[name="quantity"]');
    if (!input) return 1;
    var n = parseInt(input.value, 10);
    if (!isFinite(n) || n < 1) return 1;
    return Math.floor(n);
  }

  function readWidgetProps(form) {
    /* Read the rental values FlexCon has put in its own hidden inputs for
       this form. If we cannot find them we add nothing — never guess. */
    var out = {};
    (form || document)
      .querySelectorAll('input[name^="properties[_flex_conversion"]')
      .forEach(function (i) {
        var m = i.name.match(/properties\[(.+)\]/);
        if (m) out[m[1]] = i.value;
      });
    return out;
  }

  /* The price shown here is lifted from the selected radio's own label, which
     Liquid already rendered through the `money` filter. Nothing is formatted in
     JS, so there is no hard-coded currency symbol, decimal separator or
     multiplication to drift from the store's real money format. */
  function updateSummary() {
    var box = document.querySelector('[data-se-tx-summary]');
    if (!box) return;
    var scope = box.closest('form') || document;
    var n = selectedTxCount(scope);
    var checked = scope.querySelector('[data-se-tx-input]:checked');
    var option = checked && checked.closest ? checked.closest('.se-tx__option') : null;
    var priceEl = option ? option.querySelector('.se-tx__price') : null;
    var price = priceEl ? priceEl.textContent.replace(/^\s*\+\s*/, '').trim() : '';
    box.textContent =
      'Adds ' + n + ' transmitter' + (n > 1 ? 's' : '') + (price ? ' — ' + price : '');
  }
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-se-tx-input]')) updateSummary();
  });
  if (document.querySelector('[data-se-tx]')) {
    if (document.readyState !== 'loading') updateSummary();
    else document.addEventListener('DOMContentLoaded', updateSummary);
  }

  /* An add that Shopify rejects must say so, not reload as though it worked. */
  function formError(form, message) {
    var el = form.querySelector('[data-se-tx-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'se-tx-error';
      el.setAttribute('data-se-tx-error', '');
      el.setAttribute('role', 'alert');
      el.setAttribute('tabindex', '-1');
      var fs = form.querySelector('[data-se-tx]');
      if (fs && fs.parentNode) fs.parentNode.insertBefore(el, fs.nextSibling);
      else form.appendChild(el);
    }
    el.textContent = message; /* untrusted-safe: text only */
    el.hidden = false;
    el.focus();
  }
  function clearFormError(form) {
    var el = form.querySelector('[data-se-tx-error]');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
  }

  /* Intercept the headset Add to cart and issue ONE paired /cart/add.js.
     NOTE: a single multi-item add was OBSERVED to behave atomically on this
     store; that is not a documented guarantee, so we re-read /cart.js after
     every attempt — success or failure — and the cart guard re-validates the
     whole cart from that fresh copy. */
  window.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button[name="add"]');
      if (!btn) return;
      var form = headsetForm(btn);
      if (!form) return; /* not a headset form */
      var props = readWidgetProps(form);
      if (!props._flex_conversion_start_date) return; /* no date yet — the date guard handles it */

      e.preventDefault();
      e.stopImmediatePropagation();
      clearFormError(form);

      var wantTx = selectedTxCount(form);
      var wantHeadsets = headsetQty(form);

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var key = groupKey(props);
          var g = analyse(cart).groups[key];
          var already = g ? g.transmitters : 0;
          /* Already covered for this exact date group → add no extra
             transmitter. The rule is one MINIMUM per date group, never one
             per headset box, so wantHeadsets deliberately plays no part here. */
          var need = Math.max(0, wantTx - already);
          var items = [{ id: headsetVariantId(form), quantity: wantHeadsets, properties: props }];
          if (need > 0) items.push({ id: TX, quantity: need, properties: txPropsFrom(props) });
          return fetch(ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
          });
        })
        .then(readJson)
        .then(function (res) {
          /* Re-read the cart after EVERY attempt, so the guard below always
             works from what the cart really contains. */
          return fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) { return { res: res, cart: cart }; });
        })
        .then(function (o) {
          if (!o.res.ok) {
            formError(form, shopifyError(o.res.data, o.res.status));
            render(o.cart);
            return;
          }
          document.dispatchEvent(new CustomEvent('cart:update'));
          window.location.reload();
        })
        .catch(function () {
          formError(form, 'Sorry — we could not reach the cart. Please check your connection and try again.');
        });
    },
    true
  );

  /* ---------------- cart / drawer guard ---------------- */
  /* token → real group key. The key is cart-derived, so it never touches the
     DOM; only the token we mint here does. */
  var groupTokens = Object.create(null);
  var tokenSeq = 0;

  function warningEl(container) {
    var el = container.querySelector('[data-se-tx-warning]');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'se-tx-warning';
    el.setAttribute('data-se-tx-warning', '');
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('tabindex', '-1');
    container.insertBefore(el, container.firstChild);
    return el;
  }

  /* Built entirely with createElement/textContent. No cart-derived value is
     ever concatenated into markup or written into an attribute. */
  function fillWarning(el, group, token) {
    while (el.firstChild) el.removeChild(el.firstChild);

    var text = document.createElement('p');
    text.className = 'se-tx-warning__text';
    text.textContent = MSG;
    el.appendChild(text);

    var when = displayableDate(group.props._flex_conversion_start_date);
    if (when) {
      var dates = document.createElement('p');
      dates.className = 'se-tx-warning__dates';
      dates.textContent = 'Rental date: ' + when;
      el.appendChild(dates);
    }

    var fix = document.createElement('button');
    fix.type = 'button';
    fix.className = 'se-tx-warning__fix';
    fix.dataset.seTxFix = token; /* internal token, matches /^g\d+$/ */
    fix.textContent = 'Add required transmitter';
    el.appendChild(fix);
  }

  function setCheckoutBlocked(blocked) {
    /* Standard checkout buttons */
    document.querySelectorAll('button[name="checkout"]').forEach(function (b) {
      if (blocked) {
        b.setAttribute('data-se-tx-blocked', 'true');
        b.disabled = true;
      } else if (b.getAttribute('data-se-tx-blocked')) {
        b.removeAttribute('data-se-tx-blocked');
        /* Respect a pickup-preference update that is still in flight. */
        if (
          b.getAttribute('data-se-pickup-busy') !== 'true' &&
          b.getAttribute('data-se-window-blocked') !== 'true'
        ) {
          b.disabled = false;
        }
      }
    });
    /* Express/accelerated buttons the theme can see (PayPal etc.). We can only
       visually disable and intercept these — they are NOT reliably blocked. */
    document
      .querySelectorAll('.additional-checkout-buttons, [data-shopify="dynamic-checkout-cart"]')
      .forEach(function (el) {
        el.setAttribute('data-se-tx-blocked', blocked ? 'true' : 'false');
        el.setAttribute('aria-hidden', blocked ? 'true' : 'false');
      });
  }

  function render(cart) {
    var containers = document.querySelectorAll('[data-se-pickup]');
    if (!containers.length) return;
    var res = analyse(cart);
    var blocked = res.invalid.length > 0;
    var token = '';
    if (blocked) {
      token = 'g' + tokenSeq++;
      groupTokens[token] = res.invalid[0].key;
    }
    containers.forEach(function (pickup) {
      var host = pickup.parentElement;
      var el = warningEl(host);
      if (!blocked) {
        el.hidden = true;
        while (el.firstChild) el.removeChild(el.firstChild);
        return;
      }
      el.hidden = false;
      fillWarning(el, res.invalid[0], token);
    });
    setCheckoutBlocked(blocked);

    /* The warning changes the height of the cart drawer's summary block. That
       block can be `position: sticky; bottom: 0`, and a sticky summary taller
       than its own scrollport squeezes the cart line items to zero height, so
       neither they nor the checkout button can be scrolled to. Horizon
       re-measures on this event (assets/cart-drawer.js). */
    document.dispatchEvent(new CustomEvent('cart-summary:changed'));
  }

  function refresh() {
    if (!document.querySelector('[data-se-pickup]')) return;
    fetch('/cart.js').then(function (r) { return r.json(); }).then(render).catch(function () {});
  }

  /* Correction action — adds ONE transmitter with that group's exact dates. */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-se-tx-fix]');
    if (!b) return;
    e.preventDefault();
    b.disabled = true;
    var key = groupTokens[b.dataset.seTxFix];
    if (typeof key !== 'string') {
      b.disabled = false;
      return;
    }
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var g = analyse(cart).groups[key];
        if (!g) return null;
        return fetch(ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: TX, quantity: 1, properties: txPropsFrom(g.props) }] }),
        }).then(readJson);
      })
      .then(function (res) {
        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) { return { res: res, cart: cart }; });
      })
      .then(function (o) {
        if (!o.res || !o.res.ok) {
          /* Say what went wrong instead of reloading as though it worked. */
          var host = b.parentElement;
          b.disabled = false;
          if (host) {
            var msg = document.createElement('p');
            msg.className = 'se-tx-warning__error';
            msg.textContent = o.res
              ? shopifyError(o.res.data, o.res.status)
              : 'That rental date is no longer in your cart. Please check your cart and try again.';
            host.appendChild(msg);
            if (host.focus) host.focus();
          }
          render(o.cart);
          return;
        }
        window.location.reload();
      })
      .catch(function () {
        b.disabled = false;
      });
  });

  /* Hold a checkout click that slips through while a group is invalid. */
  window.addEventListener(
    'click',
    function (e) {
      var t = e.target && e.target.closest && e.target.closest('button[name="checkout"], .additional-checkout-buttons, [data-shopify="dynamic-checkout-cart"]');
      if (!t) return;
      if (t.getAttribute('data-se-tx-blocked') === 'true' || t.closest('[data-se-tx-blocked="true"]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var w = document.querySelector('[data-se-tx-warning]');
        if (w && !w.hidden) w.focus();
      }
    },
    true
  );

  if (document.readyState !== 'loading') refresh();
  else document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('cart:update', refresh);
})();

/* ---------------------------------------------------------------------------
   Hire terms & pickup dialog (snippets/se-hire-terms.liquid).

   Replaces FlexCon's own rental-policy modal, which is hidden in CSS because it
   describes shipping, delivery and collection from the customer's address —
   none of which this business does. FlexCon itself is untouched.

   Native <dialog>, so Escape, focus trapping and the backdrop are the browser's.
   --------------------------------------------------------------------------- */
(function () {
  function dialogFor(trigger) {
    var id = trigger.getAttribute('data-se-terms-open');
    return id ? document.getElementById(id) : null;
  }

  document.addEventListener('click', function (event) {
    var open = event.target.closest && event.target.closest('[data-se-terms-open]');
    if (open) {
      var dlg = dialogFor(open);
      if (dlg) {
        event.preventDefault();
        if (typeof dlg.showModal === 'function') {
          dlg.showModal();
        } else {
          dlg.setAttribute('open', '');
        }
      }
      return;
    }

    var close = event.target.closest && event.target.closest('[data-se-terms-close]');
    if (close) {
      var owner = close.closest('dialog');
      if (owner) owner.close();
      return;
    }

    /* Click on the backdrop: the dialog element itself fills the whole viewport,
       so a click that lands on it rather than on the panel is outside. */
    if (event.target.matches && event.target.matches('dialog.se-terms__dialog')) {
      event.target.close();
    }
  });
})();

/* Stale / expired rental cart recovery — progressive enhancement only.
   The notice and its recovery forms (snippets/se-stale-rental-notice.liquid) are
   server-rendered and fully functional with NO JavaScript: the "Clear cart"
   button is a plain POST /cart/clear and each "Remove" is a plain POST
   /cart/change to quantity 0. This layer only improves the clear path so the
   customer lands back on the rental product page to pick fresh dates instead of
   on an empty /cart. It never rewrites dates, adds lines, or touches FlexCon
   enforcement. If anything here fails, the native form submit still runs. */
(function () {
  'use strict';

  function moveFocusToNotice() {
    var notice = document.querySelector('[data-se-stale-rental]');
    if (notice && typeof notice.focus === 'function') {
      notice.focus({ preventScroll: false });
    }
  }
  if (document.readyState !== 'loading') {
    moveFocusToNotice();
  } else {
    document.addEventListener('DOMContentLoaded', moveFocusToNotice);
  }
  /* The cart drawer re-renders on update; re-focus the notice if it reappears. */
  document.addEventListener('cart:update', moveFocusToNotice);

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('.se-stale-rental__clear')) return;
      var notice = form.closest('[data-se-stale-rental]');
      var restartUrl = notice && notice.getAttribute('data-se-restart-url');
      if (!restartUrl) return; // no target -> let the native POST /cart/clear run

      /* Take over only to redirect onward after a successful clear. */
      e.preventDefault();
      var btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;

      fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      })
        .then(function (res) {
          if (!res.ok) throw new Error('clear failed');
          window.location.assign(restartUrl);
        })
        .catch(function () {
          /* Fall back to the guaranteed native path (full-page POST /cart/clear). */
          if (btn) btn.disabled = false;
          if (typeof form.submit === 'function') form.submit();
        });
    },
    false
  );
})();
